// Dashboard.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, User, Filter, RotateCw, Monitor, Menu, X } from 'lucide-react';
import { MapContainer, TileLayer, Popup, CircleMarker, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import wqmsService from '../services/wqmsService';
import parameterRanges from '../config/parameterRanges';
import 'leaflet/dist/leaflet.css';
import PermissionsManager from './PermissionsManager';
import { MENU_ITEMS } from '../config/permissionResources';
// import UsersDashboard from './UsersDashboard';
import { useNavigate } from "react-router-dom";
import ActivityLogs from './ActivityLogs';

// Fix default markers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import SourceSignaturePanel from "../components/SourceSignaturePanel";
import { computeWqiEPA } from "../utils/wqiWeightedArithmetic";
const roleLabel = {
  admin: 'Super Admin',
  editor: 'Admin',
  viewer: 'Viewer',
};

const getRoleLabel = (role) => roleLabel[String(role || '').toLowerCase()] || role;
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const SIDE_OPTIONS = ["All", "North", "South"];
const RIVER_OPTIONS = ["All", "Ravi", "Chenab", "Jhelum", "Sutlej", "Indus", "Punjnad"];
const DRAIN_OPTIONS = ["All", "Yes", "No"];

/** WQI color per your legend */
const getWqiColor = (wqi) => {
  const n = Number(wqi);
  if (!Number.isFinite(n)) return '#718096'; // No data
  if (n >= 85) return '#7e22ce';             // Excellent
  if (n >= 70) return '#22c55e';             // Good
  if (n >= 50) return '#eab308';             // Moderate
  return '#991b1b';                          // Poor
};

const getWqiColorEPA = (wqi) => {
  const n = Number(wqi);
  if (!Number.isFinite(n)) return '#9ca3af'; // No data

  if (n <= 25) return '#16a34a';   // Excellent
  if (n <= 50) return '#22c55e';   // Good
  if (n <= 75) return '#f59e0b';   // Poor
  if (n <= 100) return '#ef4444';  // Very Poor
  return '#7f1d1d';                // Unsuitable
};

/** Label icon: Circle + Station name (WQMS-1 etc) */
const makeWqiLabelIcon = (name, wqi, color) =>
  L.divIcon({
    className: "wqi-label",
    html: `
      <div class="wqi-label__wrap">
        <div class="wqi-label__circle" style="background:${color}; border-color:${color};">
          ${wqi ?? "--"}
        </div>
        <div class="wqi-label__name">${String(name ?? "").replaceAll("<", "&lt;").replaceAll(">", "&gt;")}</div>
      </div>
    `,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });

const Dashboard = () => {
  const [side, setSide] = useState("All");
  const [river, setRiver] = useState("All");
  const [drain, setDrain] = useState("All");

  const { user, logout, hasAccess, isAdmin, allowedResources } = useAuth();
  const [stations, setStations] = useState([]);
  const [stats, setStats] = useState(null);

  // time controls
  const [mode, setMode] = useState('relative'); // 'relative' | 'absolute'
  const [relativeMinutes, setRelativeMinutes] = useState(60);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [at, setAt] = useState('');
  const [refreshMs, setRefreshMs] = useState(300000); // 5m
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedStation, setSelectedStation] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [statsAnimKey, setStatsAnimKey] = useState(0);
  const hasAnimatedStatsRef = useRef(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  // const [showUsers, setShowUsers] = useState(false);
  const [absDraftFrom, setAbsDraftFrom] = useState('');
  const [absDraftTo, setAbsDraftTo] = useState('');
const [showActivityLogs, setShowActivityLogs] = useState(false);
const [showMenuDrawer, setShowMenuDrawer] = useState(false);
  const nav = useNavigate();
  const handleLogout = () => logout();
const canManagePermissions = isAdmin || hasAccess('permissions:manage');
const canViewActivityLogs = user?.role === 'admin' || user?.role === 'editor';

const visibleMenuItems = useMemo(() => {
  return MENU_ITEMS.filter((item) => isAdmin || hasAccess(item.permission));
}, [isAdmin, hasAccess, allowedResources]);

const menuSections = useMemo(() => {
  const sections = new Map();
  visibleMenuItems.forEach((item) => {
    if (!sections.has(item.section)) sections.set(item.section, []);
    sections.get(item.section).push(item);
  });
  return sections;
}, [visibleMenuItems]);

const canSeeStation = (stationId) => {
  return isAdmin || hasAccess('stations') || hasAccess(`station:${stationId}`);
};

  const visibleStations = useMemo(
    () => (stations || []).filter((s) => canSeeStation(s.station_id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stations, isAdmin, allowedResources]
  );
  // choose the best last timestamp field
  const getLatestTs = (s) => s.latest_record_time || s.timestamp || s.last_seen || null;

  // return a Date for "range end"
  const getRangeEnd = () => (at ? new Date(at) : new Date());

  // online if last timestamp within threshold minutes
  const isStationOnline = (s, minutes = Number(relativeMinutes || 60)) => {
    const raw = getLatestTs(s);
    if (!raw) return false;
    const end = getRangeEnd().getTime();
    const last = new Date(raw).getTime();
    const diffMin = (end - last) / (1000 * 60);
    return diffMin <= minutes;
  };

  const isInstalledButQuiet = (s) => Boolean(s.is_installed) && !isStationOnline(s);

  const renderTimestamp = (ts) => {
    if (ts == null) return '—';
    const s = String(ts);
    const formatTs = x =>
      x.replace('T', ' ')
        .replace(/Z$/, '')
        .replace(/\.\d+$/, '');
    return s.includes('T') ? formatTs(s) : formatTs(s);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setIsRefreshing(true);
    setError("");

    let fromParam, toParam, atParam;
    if (mode === "relative") {
      if (at) {
        const end = new Date(at);
        const start = new Date(end);
        start.setMinutes(start.getMinutes() - Number(relativeMinutes));
        fromParam = start.toISOString();
        toParam = end.toISOString();
      } else {
        const now = new Date();
        const start = new Date(now.getTime() - Number(relativeMinutes) * 60 * 1000);
        fromParam = start.toISOString();
        toParam = now.toISOString();
      }
    } else {
      if (from) {
        const fromDate = new Date(from);
        fromDate.setHours(0, 0, 0, 0);
        fromParam = fromDate.toISOString();
      } else {
        fromParam = undefined;
      }

      if (to) {
        const toDate = new Date(to);
        toDate.setHours(23, 59, 59, 999);
        toParam = toDate.toISOString();
      } else {
        toParam = undefined;
      }

      atParam = at ? new Date(at).toISOString() : undefined;
    }

    const hasWindow = !!(fromParam || toParam || atParam);
    const quickRangeMinutes = String(relativeMinutes || 10);
    const timeRangeArg = hasWindow ? undefined : quickRangeMinutes;

    try {
      const [stationsList, statsResp] = await Promise.all([
        wqmsService.getStations({ timeRange: "all", force: true, side, river, drain }),
        wqmsService.getStats(timeRangeArg, atParam, fromParam, toParam),
      ]);

      setStations(Array.isArray(stationsList) ? stationsList : []);
      if (statsResp?.success) setStats(statsResp.data);
      else if (statsResp && statsResp.success === false) throw new Error(statsResp.message || "Failed to fetch stats");
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch data from database");
    } finally {
      setLoading(false);
      setTimeout(() => {
        setIsRefreshing(false);
        if (!hasAnimatedStatsRef.current) {
          setStatsAnimKey(k => k + 1);
          hasAnimatedStatsRef.current = true;
        }
      }, 150);
    }
  }, [mode, relativeMinutes, from, to, at, side, river, drain]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (mode === 'relative' && at) setAt(''); }, [mode, at]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchData, refreshMs);
    return () => clearInterval(id);
  }, [autoRefresh, refreshMs, mode, relativeMinutes, from, to, at, side, river, drain, fetchData]);

  const formatCurrentRangeLabel = () => {
    if (mode === 'relative' && !at) {
      const m = Number(relativeMinutes);
      const now = new Date();
      const start = new Date(now.getTime() - m * 60 * 1000);
      let desc = '';
      if (m < 60) desc = `Last ${m} minutes`;
      else if (m % 60 === 0 && m < 1440) desc = `Last ${m / 60} hour${m === 60 ? '' : 's'}`;
      else if (m % 1440 === 0) desc = `Last ${m / 1440} day${m === 1440 ? '' : 's'}`;
      else desc = `Last ${m} minutes`;
      return `${desc} (${start.toLocaleDateString()})`;
    }
    if (mode === 'relative' && at) {
      const endDate = new Date(at);
      const startDate = new Date(at);
      startDate.setMinutes(startDate.getMinutes() - Number(relativeMinutes));
      return `Last ${relativeMinutes} min to ${endDate.toLocaleDateString()} (${startDate.toLocaleDateString()})`;
    }
    if (mode === 'absolute' && from && to) {
      const fromDate = new Date(from);
      return `Custom range (${fromDate.toLocaleDateString()})`;
    }
    return 'Select time range';
  };

  const quickRanges = [
    { label: 'Last 1 hour', minutes: 60 },
    { label: 'Last 2 hour', minutes: 120 },
    { label: 'Last 3 hours', minutes: 180 },
    { label: 'Last 4 hour', minutes: 240 },
    { label: 'Last 5 hour', minutes: 300 },
    { label: 'Last 6 hours', minutes: 360 },
    { label: 'Last 12 hours', minutes: 720 },
    { label: 'Last 24 hours', minutes: 1440 },
    { label: 'Last 1 month', minutes: 30 * 24 * 60 },
    { label: 'Last 3 months', minutes: 90 * 24 * 60 },
    { label: 'Last 6 months', minutes: 180 * 24 * 60 },
    { label: 'Last 1 year', minutes: 365 * 24 * 60 },
    { label: 'Last 5 years', minutes: 5 * 365 * 24 * 60 },
    { label: 'Fiscal year to date (PK)', preset: 'fy_ytd' },
  ];

  const formatValue = (value, unit = '') => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const n = Number(value);
    if (!Number.isFinite(n)) return 'N/A';
    return `${n.toFixed(2)}${unit}`;
  };

  const parameterCards = [
    { key: 'ammonia_mg_l_avg', label: 'Ammonia (NH3)', unit: ' mg/L' },
    { key: 'bod_mg_l_avg', label: 'BOD', unit: ' mg/L' },
    { key: 'cod_mg_l_avg', label: 'COD', unit: ' mg/L' },
    { key: 'chloride_mg_l_avg', label: 'Chloride', unit: ' mg/L' },
    { key: 'color_hazen_avg', label: 'Color', unit: ' Hazen' },
    { key: 'dissolved_oxygen_mg_l_avg', label: 'Dissolved Oxygen', unit: ' mg/L' },
    { key: 'toc_mg_l_avg', label: 'TOC', unit: ' mg/L' },
    { key: 'doc_mg_l_avg', label: 'DOC', unit: ' mg/L' },
    { key: 'ph_avg', label: 'pH', unit: '' },
    { key: 'redox_mv_avg', label: 'Redox/ORP', unit: ' mV' },
    { key: 'temperature_c_avg', label: 'Temperature', unit: ' °C' },
    { key: 'tss_mg_l_avg', label: 'TSS', unit: ' mg/L' },
    { key: 'turbidity_ntu_avg', label: 'Turbidity', unit: ' NTU' },
    { key: 'conductivity_us_cm_avg', label: 'Conductivity', unit: ' µS/cm' },
  ];

  const overallAverages = useMemo(() => {
    if (!stations || stations.length === 0) return {};
    const sums = {};
    const counts = {};
    for (const p of parameterCards) {
      sums[p.key] = 0;
      counts[p.key] = 0;
    }
    for (const s of stations) {
      for (const p of parameterCards) {
        const n = Number(s[p.key]);
        if (Number.isFinite(n)) {
          sums[p.key] += n;
          counts[p.key] += 1;
        }
      }
    }
    const avg = {};
    for (const p of parameterCards) {
      avg[p.key] = counts[p.key] > 0 ? sums[p.key] / counts[p.key] : null;
    }
    return avg;
  }, [stations]);

  const sourceSigAverages = useMemo(() => ({
    nh3_mg_l: overallAverages['ammonia_mg_l_avg'] ?? undefined,
    bod_mg_l: overallAverages['bod_mg_l_avg'] ?? undefined,
    cod_mg_l: overallAverages['cod_mg_l_avg'] ?? undefined,
    chloride_mg_l: overallAverages['chloride_mg_l_avg'] ?? undefined,
    color_hazen: overallAverages['color_hazen_avg'] ?? undefined,
    do_mg_l: overallAverages['dissolved_oxygen_mg_l_avg'] ?? undefined,
    toc_mg_l: overallAverages['toc_mg_l_avg'] ?? undefined,
    doc_mg_l: overallAverages['doc_mg_l_avg'] ?? undefined,
    ph: overallAverages['ph_avg'] ?? undefined,
    orp_mv: overallAverages['redox_mv_avg'] ?? undefined,
    temp_c: overallAverages['temperature_c_avg'] ?? undefined,
    tss_mg_l: overallAverages['tss_mg_l_avg'] ?? undefined,
    turbidity_ntu: overallAverages['turbidity_ntu_avg'] ?? undefined,
    ec_us_cm: overallAverages['conductivity_us_cm_avg'] ?? undefined,
  }), [overallAverages]);

  const hasSourceSigData = useMemo(() => {
    return Object.values(sourceSigAverages).some(v => Number.isFinite(Number(v)));
  }, [sourceSigAverages]);

  const computeWqi = (stationsList) => {
    if (!stationsList || stationsList.length === 0) return null;

    const toNum = (v) => (v === null || v === undefined ? NaN : Number(v));
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    const paramAvg = {};
    for (const p of parameterCards) {
      const vals = stationsList.map((s) => toNum(s[p.key])).filter((n) => Number.isFinite(n));
      if (vals.length) paramAvg[p.key] = vals.reduce((a, b) => a + b, 0) / vals.length;
    }

    const subindex = (key, value) => {
      if (!Number.isFinite(value)) return null;
      const conf = parameterRanges[key];
      if (!conf) return null;

      const [gmin, gmax] = conf.good;
      const [wmin, wmax] = conf.warn;

      if (value >= Math.min(gmin, gmax) && value <= Math.max(gmin, gmax)) return 100;

      if (value >= Math.min(wmin, wmax) && value <= Math.max(wmin, wmax)) {
        const edgeGood = value < gmin ? gmin : gmax;
        const edgeWarn = value < gmin ? wmin : wmax;
        const width = Math.abs(edgeGood - edgeWarn) || 1;
        const dist = Math.abs(value - edgeGood);
        return clamp(100 * (1 - dist / width), 0, 100);
      }
      return 0;
    };

    const subs = [];
    for (const p of parameterCards) {
      const s = subindex(p.key, paramAvg[p.key]);
      if (Number.isFinite(s) && s > 0) subs.push(s);
    }
    if (!subs.length) return null;

    const product = subs.reduce((prod, s) => prod * (s / 100), 1);
    const gm = Math.pow(product, 1 / subs.length) * 100;
    return Math.round(gm);
  };

  useEffect(() => {
    if (stations.length > 0) {
      console.log("WQI INPUT SAMPLE:", stations[0]);
    }
  }, [stations]);

  const wqiOverall = useMemo(
    () => computeWqiEPA(stations),
    [stations]
  );

  return (
    <div className="App">
      <style>{`
        /* Hide time filter, auto-refresh dropdown, and refresh button only */
        .time-filter > svg:first-child,
        .time-filter > button.time-select,
        .time-filter > span,
        .time-filter > select.time-select,
        .time-filter > button.refresh-button {
          display: none !important;
        }

        .wqi-label { background: transparent !important; border: none !important; }
        .wqi-label__wrap{
          display:flex; flex-direction:column; align-items:center;
          transform: translate(-50%, -50%);
          pointer-events:none;
        }
        .wqi-label__circle{
          width:26px; height:26px; border-radius:9999px;
          display:flex; align-items:center; justify-content:center;
          color:#fff; font-weight:800; font-size:12px;
          border:2px solid;
          box-shadow:0 2px 6px rgba(0,0,0,0.25);
        }
        .wqi-label__name{
          margin-top:3px;
          padding:2px 8px;
          border-radius:9999px;
          background: rgba(255,255,255,0.92);
          border:1px solid rgba(0,0,0,0.12);
          font-size:11px;
          font-weight:700;
          color:#111827;
          white-space:nowrap;
        }

        .dashboard-watermark{
          position:absolute;
          inset:0;
          display:flex;
          align-items:center;
          justify-content:center;
          pointer-events:none;
          z-index:20;
          overflow:hidden;
        }

        .dashboard-watermark__text{
          transform: rotate(-32deg);
          font-size: clamp(90px, 18vw, 260px);
          font-weight: 900;
          color: rgba(60, 60, 60, 0.10);
          letter-spacing: 0.12em;
          text-transform: uppercase;
          user-select: none;
          white-space: nowrap;
        }

        .dashboard-content{
          position:relative;
          z-index:30;
        }
.drawer-overlay{
  position:fixed;
  inset:0;
  background:rgba(15,23,42,0.35);
  z-index:9998;
}

.menu-drawer{
  position:fixed;
  top:0;
  right:0;
  width:min(360px, 92vw);
  height:100vh;
  background:linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  z-index:9999;
  box-shadow:-12px 0 30px rgba(37,99,235,0.15);
  padding:22px;
  display:flex;
  flex-direction:column;
  gap:12px;
}

.drawer-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  margin-bottom:10px;
}

.drawer-item{
  width:100%;
  text-align:left;
  border:0;
  border-radius:12px;
  padding:13px 14px;
  background:#f8fafc;
  color:#1f2937;
  font-weight:700;
  cursor:pointer;
}

.drawer-item:hover{
  background:#e0f2fe;
}

.menu-drawer-footer{
  margin-top:auto;
  padding-top:14px;
  border-top:1px solid #e2e8f0;
}

.drawer-item-danger{
  background:#fef2f2 !important;
  color:#b91c1c !important;
  display:flex;
  align-items:center;
  gap:10px;
}

.drawer-item-danger:hover{
  background:#fee2e2 !important;
}


        .demo-badge{
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding:8px 14px;
          border-radius:9999px;
          background: linear-gradient(135deg, #2563eb, #4f46e5);
          color:#fff;
          font-size:13px;
          font-weight:700;
          box-shadow:0 6px 18px rgba(37,99,235,0.28);
          border:2px solid rgba(255,255,255,0.75);
        }
      `}</style>

      <div
        className={`wqms-dashboard ${isRefreshing ? 'refreshing' : ''}`}
        style={{ position: 'relative', overflow: 'hidden' }}
      >
        <div className="dashboard-watermark">
          <div className="dashboard-watermark__text">DEMO</div>
        </div>

        <div className="dashboard-content">
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 24px",
              borderBottom: "1px solid #e5e7eb",
              background: "linear-gradient(180deg,#f8fafc,#ffffff)",
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#334155" }}>WQMS Dashboard</div>

                <span className="demo-badge">
                  <Monitor size={16} />
                  Demo Version
                </span>
              </div>

              <div style={{ fontSize: 14, color: "#64748b" }}>Water Quality Monitoring System</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, color: "#475569", fontSize: 12 }}>
                <User size={16} />
                <span>{user?.firstName} {user?.lastName}</span>
		<span className="chip">{getRoleLabel(user?.role)}</span>

              </div>
            </div>

 	      <div className="actions-bar" style={{ display: "flex", gap: 12, alignItems: "center" }}>
		<button
  className="btn"
  onClick={() => setShowMenuDrawer(true)}
  title="Open menu"
  style={{ display: "flex", alignItems: "center", gap: 8 }}
>
  <Menu size={16} />
  Menu
</button>


{/* old top buttons hidden because drawer menu is now used */}
<div style={{ display: "none" }}>
              <button className="btn" onClick={() => nav('/reports/river-longitudinal')} title="Rivers Upstream Downstream Analysis">
                Rivers Upstream Downstream Analysis
              </button>
              <button className="btn" onClick={() => nav('/reports/river-vs-river')} title="Rivers Comparison">Rivers Comparison</button>
              <button className="btn" onClick={() => nav('/reports/river-compare')} title="Parameters Comparison">Parameters Comparison</button>
              <button className="btn" onClick={() => nav('/reports/compare-2y')} title="2Y Plot">2Y Plot</button>
              <button className="btn" onClick={() => nav('/reports/monthly-wqi')} title="Monthly WQI Calendar">Monthly WQI</button>
              <button className="btn" onClick={() => nav('/reports/exceedance')} title="Regulatory Exceedance Summary">Exceedance Report</button>
{canManagePermissions && (
  <button
    className="btn"
    onClick={() => setShowPermissions(true)}
    title="Manage Roles & Permissions"
  >
    Roles / Permissions
  </button>
)}
{canManagePermissions && (
 <button
  className="btn"
  onClick={() => nav('/users')}
  title="Manage Users"
>
  Users
</button>
)}
{canViewActivityLogs && (
  <button
    className="btn"
    onClick={() => setShowActivityLogs(true)}
    title="View Activity Logs"
  >
    Activity Logs
  </button>
)}</div>

            </div>
          </div>

          {/* Controls */}
          <div
            className="dashboard-controls"
            style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="time-filter" style={{ position: 'relative', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Filter size={20} />
                <button
                  className="time-select"
                  onClick={() => {
                    setShowTimePicker((s) => !s);
                    setAbsDraftFrom(from);
                    setAbsDraftTo(to);
                  }}
                >
                  {formatCurrentRangeLabel()}
                </button>

                {showTimePicker && ReactDOM.createPortal(
                  <div style={{ position: 'fixed', inset: 0, zIndex: 2147483000 }}>
                    <div onClick={() => setShowTimePicker(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.1)' }} />
                    <div style={{
                      position: 'absolute',
                      top: '10vh',
                      left: 16,
                      zIndex: 2147483001,
                      background: 'rgba(255, 255, 255, 0.97)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 12,
                      padding: 10,
                      boxShadow: '0 32px 64px rgba(0,0,0,0.2)',
                      width: 'min(420px, 92vw)',
                      maxHeight: '80vh',
                      overflowY: 'auto'
                    }}>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ minWidth: 170 }}>
                          <div style={{
                            fontWeight: 700, marginBottom: 8, fontSize: 13,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
                          }}>Quick ranges</div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {quickRanges.map((r, idx) => (
                              <button
                                key={idx}
                                className="time-select"
                                style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11 }}
                                onClick={() => {
                                  if (r.preset === 'fy_ytd') {
                                    const now = new Date();
                                    const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
                                    const start = new Date(Date.UTC(year, 6, 1, 0, 0, 0));
                                    setMode('absolute');
                                    setFrom(start.toISOString().slice(0, 10));
                                    setTo(now.toISOString().slice(0, 10));
                                    setAt('');
                                  } else if (r.minutes) {
                                    setMode('relative');
                                    setAt('');
                                    setRelativeMinutes(r.minutes);
                                  }
                                  setShowTimePicker(false);
                                }}
                              >
                                {r.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div style={{ minWidth: 220 }}>
                          <div style={{
                            fontWeight: 700, marginBottom: 8, fontSize: 13,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                          }}>
                            Absolute date range
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                            <label>
                              <div style={{ fontSize: 11, color: '#4a5568', marginBottom: 4 }}>From</div>
                              <input
                                type="date"
                                className="time-select"
                                style={{ padding: '6px 8px', fontSize: 11 }}
                                value={absDraftFrom}
                                onChange={(e) => setAbsDraftFrom(e.target.value)}
                              />
                            </label>

                            <label>
                              <div style={{ fontSize: 11, color: '#4a5568', marginBottom: 4 }}>To</div>
                              <input
                                type="date"
                                className="time-select"
                                style={{ padding: '6px 8px', fontSize: 11 }}
                                value={absDraftTo}
                                onChange={(e) => setAbsDraftTo(e.target.value)}
                              />
                            </label>

                            <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                              <button
                                className="refresh-btn"
                                style={{ padding: '6px 8px', fontSize: 11 }}
                                onClick={() => {
                                  setMode('absolute');
                                  setFrom(absDraftFrom || '');
                                  setTo(absDraftTo || '');
                                  setAt('');
                                  setShowTimePicker(false);
                                }}
                              >
                                Apply date range
                              </button>
                              <button
                                className="time-select"
                                style={{ padding: '6px 8px', fontSize: 11 }}
                                onClick={() => setShowTimePicker(false)}
                              >
                                Close
                              </button>
                            </div>

                            <div style={{ fontSize: 11, color: '#4a5568' }}>Browser Time: Pakistan, PKT</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}

                <span style={{ marginLeft: 8, color: '#64748b', fontSize: 12 }}>Auto-refresh:</span>
                <select
                  value={refreshMs}
                  onChange={(e) => { const v = Number(e.target.value); setRefreshMs(v); setAutoRefresh(v > 0); }}
                  className="time-select"
                >
                  {[
                    { value: 0, label: 'Off' },
                    { value: 10000, label: '10s' },
                    { value: 30000, label: '30s' },
                    { value: 60000, label: '1m' },
                    { value: 300000, label: '5m' }
                  ].map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>

                <button
                  type="button"
                  className={`refresh-button ${isRefreshing ? 'loading' : ''}`}
                  onClick={() => { if (!isRefreshing) fetchData(); }}
                  disabled={isRefreshing}
                  aria-label="Refresh now"
                  title={isRefreshing ? 'Refreshing...' : 'Refresh now'}
                >
                  <RotateCw size={16} className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`} />
                </button>

                <div
                  className="filters-row"
                  style={{
                    flexBasis: '100%',
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    alignItems: 'center',
                    marginTop: 10
                  }}
                >
                  <label className="flex items-center gap-2">
                    <span className="text-sm font-medium">Side: </span>
                    <select
                      className="time-select"
                      style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff' }}
                      value={side}
                      onChange={(e) => setSide(e.target.value)}
                    >
                      {SIDE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>

                  <label className="flex items-center gap-2">
                    <span className="text-sm font-medium">River: </span>
                    <select
                      className="time-select"
                      style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff' }}
                      value={river}
                      onChange={(e) => setRiver(e.target.value)}
                    >
                      {RIVER_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>

                  <label className="flex items-center gap-2">
                    <span className="text-sm font-medium">On drain: </span>
                    <select
                      className="time-select"
                      style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff' }}
                      value={drain}
                      onChange={(e) => setDrain(e.target.value)}
                    >
                      {DRAIN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>

                  {loading && <span className="text-sm opacity-70">Loading…</span>}
                  {error && <span className="text-sm text-red-600">{error}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* KPI cards */}
          <div>
            <div
              key={statsAnimKey}
              className="stats-grid"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
            >
              {parameterCards.map((p) => (
                <div key={p.key} className="stat-card" style={{ background: 'linear-gradient(135deg, #e0f2fe 0%, #a5f3fc 100%)', color: '#111827' }}>
                  <div>
                    <h3>{formatValue(overallAverages[p.key], p.unit)}</h3>
                    <p>{p.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Source signature */}
          <div className="panel-row" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', marginTop: 7, marginBottom: 7 }}>
            <div style={{ width: '100%' }}>
              <SourceSignaturePanel averages={hasSourceSigData ? sourceSigAverages : {}} />
            </div>
          </div>

          {/* Station names banner */}
          {!loading && stations.length > 0 && (
            <div className="station-names-banner">
              {visibleStations.map((s) => (
                <a
                  key={`name-${s.station_id}`}
                  href={`/station/${s.station_id}`}
                  className="station-chip"
                  style={{
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: '8px 10px',
                    borderRadius: 12,
                    background: 'rgba(255,255,255,0.9)',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 9999,
                      marginTop: 3,
                      background: getWqiColorEPA(computeWqiEPA([s]))
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>
                      {s.site_name}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Map */}
          <div className="map-container">
            {!loading && stations.length === 0 && (
              <div style={{
                position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
                background: 'rgba(255,255,255,0.95)', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: '6px 10px', fontSize: 12, color: '#4a5568'
              }}>
                No station data in the selected time range. Try a broader range.
              </div>
            )}

            {loading ? (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Loading station data...</p>
              </div>
            ) : (
              <>
                <MapContainer
                  center={[31.5, 74.2]}
                  zoom={7}
                  minZoom={6}
                  maxZoom={12}
                  style={{ height: '100%', width: '100%' }}
                  zoomAnimation={false}
                  markerZoomAnimation={false}
                  fadeAnimation={false}
                  preferCanvas={false}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />

                  <MapResizer />
                  <FitBounds stations={visibleStations} />

                  {visibleStations.map((station) => {
                    const wqi = computeWqiEPA([station]);
                    const wqiColor = getWqiColorEPA(wqi);

                    const lat = Number(station.latitude);
                    const lng = Number(station.longitude);
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

                    const labelName = station.site_name || station.station_id;
                    const labelIcon = makeWqiLabelIcon(labelName, wqi, wqiColor);

                    return (
                      <React.Fragment key={station.station_id}>
                        <CircleMarker
                          center={[lat, lng]}
                          radius={12}
                          pathOptions={{
                            color: wqiColor,
                            fillColor: wqiColor,
                            fillOpacity: 0.9,
                            weight: 1,
                          }}
                        >
                          <Popup>
                            <div className="station-popup">
                              <h3>{station.site_name}</h3>
                              <p><strong>District:</strong> {station.district}</p>
                              <p><strong>WQI:</strong> {wqi ?? "N/A"}</p>

                              <div className="parameters-grid">
                                {parameterCards.map((p) => (
                                  <div key={p.key} className="param-item">
                                    <strong>{p.label}</strong>
                                    <span className="param-reading">
                                      {formatValue(station[p.key], p.unit)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Popup>
                        </CircleMarker>

                        <Marker
                          position={[lat, lng]}
                          icon={labelIcon}
                          interactive={false}
                          keyboard={false}
                        />
                      </React.Fragment>
                    );
                  })}
                </MapContainer>

                {/* WQI Widget */}
                <div style={{
                  position: 'absolute', right: 16, top: 60, zIndex: 1000,
                  background: 'rgba(255,255,255,0.95)', border: '1px solid #e2e8f0', borderRadius: 12,
                  padding: '12px 16px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}>
                  {(() => {
                    const wqi = wqiOverall;
                    const color = getWqiColorEPA(wqi);
                    const label =
                      wqi === null
                        ? "No data"
                        : wqi <= 25
                          ? "Excellent"
                          : wqi <= 50
                            ? "Good"
                            : wqi <= 75
                              ? "Poor"
                              : wqi <= 100
                                ? "Very Poor"
                                : "Unsuitable";

                    return (
                      <div style={{ minWidth: 140 }}>
                        <div style={{ fontSize: 12, color: '#4a5568', marginBottom: 4 }}>WQI (overall)</div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                          <span style={{ fontSize: 28, fontWeight: 800, color }}>{wqi ?? '--'}</span>
                          <span style={{ fontSize: 12, color }}>{label}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Legend */}
                <div style={{
                  position: 'absolute', left: 16, bottom: 20, zIndex: 1000,
                  background: 'rgba(255,255,255,0.95)', border: '1px solid #e2e8f0', borderRadius: 8,
                  padding: '8px 12px', boxShadow: '0 4px 8px rgba(0,0,0,0.08)'
                }}>
                  <div style={{ fontSize: 12, color: '#4a5568', marginBottom: 6, fontWeight: 600 }}>Legend (WQI)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '18px auto', columnGap: 8, rowGap: 6, alignItems: 'center' }}>
                    <span style={{ width: 14, height: 14, borderRadius: 9999, background: '#16a34a', display: 'inline-block' }} />
                    <span style={{ fontSize: 12 }}>Excellent (0 - 25)</span>
                    <span style={{ width: 14, height: 14, borderRadius: 9999, background: '#22c55e', display: 'inline-block' }} />
                    <span style={{ fontSize: 12 }}>Good (26 - 50)</span>
                    <span style={{ width: 14, height: 14, borderRadius: 9999, background: '#f59e0b', display: 'inline-block' }} />
                    <span style={{ fontSize: 12 }}>Poor (50 - 75)</span>
                    <span style={{ width: 14, height: 14, borderRadius: 9999, background: '#ef4444', display: 'inline-block' }} />
                    <span style={{ fontSize: 12 }}>Very Poor (76 - 100)</span>
                    <span style={{ width: 14, height: 14, borderRadius: 9999, background: '#7f1d1d', display: 'inline-block' }} />
                    <span style={{ fontSize: 12 }}>Unsuitable (&gt; 100)</span>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {showMenuDrawer && (
  <>
    <div className="drawer-overlay" onClick={() => setShowMenuDrawer(false)} />

    <div className="menu-drawer">
      <div className="drawer-header">
        <h2 style={{ margin: 0, fontSize: 22, color: "#1f2937" }}>Menu</h2>

        <button
          className="icon-btn"
          onClick={() => setShowMenuDrawer(false)}
          title="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      {visibleMenuItems.length === 0 ? (
        <p className="menu-drawer-empty">No menu items assigned to your account.</p>
      ) : (
        Array.from(menuSections.entries()).map(([section, items]) => (
          <div key={section} className="menu-drawer-section">
            <div className="menu-drawer-section-title">{section}</div>
            {items.map((item) => (
              <button
                key={item.permission}
                className="drawer-item"
                onClick={() => {
                  setShowMenuDrawer(false);
                  if (item.action === 'permissions') {
                    setShowPermissions(true);
                  } else if (item.path) {
                    nav(item.path);
                  }
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        ))
      )}

      {canViewActivityLogs && (
        <button
          className="drawer-item"
          onClick={() => {
            setShowMenuDrawer(false);
            setShowActivityLogs(true);
          }}
        >
          Activity Logs
        </button>
      )}

      <div className="menu-drawer-footer">
        <button
          type="button"
          className="drawer-item drawer-item-danger"
          onClick={() => {
            setShowMenuDrawer(false);
            handleLogout();
          }}
        >
          <LogOut size={18} />
          Log out
        </button>
      </div>
    </div>
  </>
)}

{/* Permissions Manager */}
{canManagePermissions && (
  <PermissionsManager
    isOpen={showPermissions}
    onClose={() => setShowPermissions(false)}
  />
)}

{canViewActivityLogs && (
  <ActivityLogs
    isOpen={showActivityLogs}
    onClose={() => setShowActivityLogs(false)}
  />
)}
          
          {/* Station Detail Modal */}
          {selectedStation && (
            <div className="modal-overlay" onClick={() => setSelectedStation(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{selectedStation.site_name}</h2>
                  <button className="close-btn" onClick={() => setSelectedStation(null)}>×</button>
                </div>

                <div className="modal-body">
                  <div className="station-info">
                    <p><strong>Station ID:</strong> {selectedStation.station_id}</p>
                    <p><strong>District:</strong> {selectedStation.district}</p>
                    <p><strong>Coordinates:</strong> {selectedStation.latitude}, {selectedStation.longitude}</p>
                    <p><strong>Last Update:</strong> {new Date(selectedStation.timestamp).toLocaleString()}</p>
                  </div>

                  <div className="parameters-detailed">
                    <h3>Water Quality Parameters</h3>
                    <div className="params-grid">
                      <div className="param-card">
                        <h4>pH</h4>
                        <span className="param-value">{formatValue(selectedStation.ph_avg)}</span>
                      </div>
                      <div className="param-card">
                        <h4>Temperature</h4>
                        <span className="param-value">{formatValue(selectedStation.temperature_c_avg, ' °C')}</span>
                      </div>
                      <div className="param-card">
                        <h4>Dissolved Oxygen</h4>
                        <span className="param-value">{formatValue(selectedStation.dissolved_oxygen_mg_l_avg, ' mg/L')}</span>
                      </div>
                      <div className="param-card">
                        <h4>Conductivity</h4>
                        <span className="param-value">{formatValue(selectedStation.conductivity_us_cm_avg, ' µS/cm')}</span>
                      </div>
                      <div className="param-card">
                        <h4>Turbidity</h4>
                        <span className="param-value">{formatValue(selectedStation.turbidity_ntu_avg, ' NTU')}</span>
                      </div>
                      <div className="param-card">
                        <h4>Ammonia (NH3)</h4>
                        <span className="param-value">{formatValue(selectedStation.ammonia_mg_l_avg, ' mg/L')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// helpers used above
const FitBounds = ({ stations }) => {
  const map = useMap();
  useEffect(() => {
    if (!stations || stations.length === 0) return;
    const valid = stations
      .map((s) => [Number(s.latitude), Number(s.longitude)])
      .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    if (!valid.length) return;
    const bounds = L.latLngBounds(valid);
    if (bounds && bounds.isValid()) {
      map.stop();
      map.fitBounds(bounds, { padding: [50, 50], animate: false });
    }
  }, [stations.length, map]);
  return null;
};

const MapResizer = () => {
  const map = useMap();
  useEffect(() => {
    const safeInvalidate = () => { try { map.invalidateSize({ animate: false }); } catch (_) {} };
    map.whenReady(() => { setTimeout(safeInvalidate, 0); setTimeout(safeInvalidate, 150); });
    window.addEventListener('resize', safeInvalidate);
    const container = map.getContainer();
    let ro;
    if (container && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => safeInvalidate());
      ro.observe(container);
    }
    return () => {
      window.removeEventListener('resize', safeInvalidate);
      if (ro) ro.disconnect();
    };
  }, [map]);
  return null;
};

export default Dashboard;
